import { Events } from './events';
import { Scene } from './scene';

type CaseMetadata = {
    caseName?: string;
    caseNumber?: string;
};

type CameraPose = {
    position?: { x?: number; y?: number; z?: number };
    target?: { x?: number; y?: number; z?: number };
};

type AnnotationLayer = {
    id?: string;
    name?: string;
    visible?: boolean;
};

type ScreenshotMetadata = {
    metadataMode: 'sidecar-json';
    caseName?: string;
    caseNumber?: string;
    captureDate: string;
    camera: {
        position: { x: number; y: number; z: number };
        target: { x: number; y: number; z: number };
    };
    visibleLayers: string[];
    overlays: {
        annotationLabels: boolean;
        measurementOverlay: boolean;
    };
};

type ScreenshotCaptureOptions = {
    scale?: 2 | 4;
    includeOverlays?: boolean;
    download?: boolean;
    returnBlob?: boolean;
};

type ScreenshotCaptureResult = {
    success: boolean;
    metadataMode: 'sidecar-json';
    baseSize: { width: number; height: number };
    outputSize: { width: number; height: number };
    requestedScale: 2 | 4;
    appliedScale: number;
    maxDimension: number;
    imageFileName: string;
    metadataFileName: string;
    metadata: ScreenshotMetadata;
    pngBlob?: Blob;
};

const sanitizeFileSegment = (value: string) => {
    const cleaned = (value ?? '').trim().replace(/[^\w-]+/g, '-').replace(/-+/g, '-').replace(/^-|-$/g, '');

    return cleaned.length > 0 ? cleaned : 'capture';
};

const formatTimestamp = (date: Date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    const seconds = String(date.getSeconds()).padStart(2, '0');
    return `${year}${month}${day}-${hours}${minutes}${seconds}`;
};

const createDownload = (blob: Blob, filename: string) => {
    const href = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = href;
    anchor.download = filename;
    anchor.click();
    URL.revokeObjectURL(href);
};

const readElementRect = (element: HTMLElement | SVGSVGElement | null) => {
    if (!element) {
        return null;
    }

    const rect = element.getBoundingClientRect();
    if (rect.width <= 0 || rect.height <= 0) {
        return null;
    }

    return {
        width: Math.max(1, Math.round(rect.width)),
        height: Math.max(1, Math.round(rect.height))
    };
};

const isElementRendered = (element: Element | null) => {
    if (!element || !element.isConnected) {
        return false;
    }

    if ((element as HTMLElement).hidden) {
        return false;
    }

    const style = window.getComputedStyle(element);
    if (style.display === 'none' || style.visibility === 'hidden' || style.visibility === 'collapse') {
        return false;
    }

    const opacity = Number.parseFloat(style.opacity || '1');
    if (Number.isFinite(opacity) && opacity <= 0) {
        return false;
    }

    const rect = element.getBoundingClientRect();
    if (rect.width <= 0 || rect.height <= 0) {
        return false;
    }

    return true;
};

const svgTextToImage = async (svgText: string): Promise<HTMLImageElement> => {
    const svgBlob = new Blob([svgText], { type: 'image/svg+xml;charset=utf-8' });
    const url = URL.createObjectURL(svgBlob);

    try {
        const image = await new Promise<HTMLImageElement>((resolve, reject) => {
            const img = new Image();
            img.onload = () => resolve(img);
            img.onerror = () => reject(new Error('failed to load overlay SVG image'));
            img.src = url;
        });
        return image;
    } finally {
        URL.revokeObjectURL(url);
    }
};

const drawSvgOverlay = async (
    context: CanvasRenderingContext2D,
    element: SVGSVGElement,
    baseSize: { width: number; height: number },
    outputSize: { width: number; height: number }
) => {
    const serialized = new XMLSerializer().serializeToString(element);
    const image = await svgTextToImage(serialized);

    context.save();
    context.scale(outputSize.width / baseSize.width, outputSize.height / baseSize.height);
    context.drawImage(image, 0, 0, baseSize.width, baseSize.height);
    context.restore();
};

const drawHtmlOverlay = (
    context: CanvasRenderingContext2D,
    element: HTMLElement,
    baseSize: { width: number; height: number },
    outputSize: { width: number; height: number }
) => {
    const overlayRect = element.getBoundingClientRect();
    if (overlayRect.width <= 0 || overlayRect.height <= 0) {
        return;
    }

    const scaleX = outputSize.width / baseSize.width;
    const scaleY = outputSize.height / baseSize.height;

    context.save();
    context.scale(scaleX, scaleY);

    const leaders = element.querySelectorAll<SVGLineElement>('line.annotation-label-leader');
    leaders.forEach((line) => {
        const x1 = Number.parseFloat(line.getAttribute('x1') ?? '0');
        const y1 = Number.parseFloat(line.getAttribute('y1') ?? '0');
        const x2 = Number.parseFloat(line.getAttribute('x2') ?? '0');
        const y2 = Number.parseFloat(line.getAttribute('y2') ?? '0');
        const stroke = line.getAttribute('stroke') ?? 'rgba(255,255,255,0.75)';
        const strokeWidth = Number.parseFloat(line.getAttribute('stroke-width') ?? '1.5');

        context.strokeStyle = stroke;
        context.lineWidth = Number.isFinite(strokeWidth) ? strokeWidth : 1.5;
        context.beginPath();
        context.moveTo(x1, y1);
        context.lineTo(x2, y2);
        context.stroke();
    });

    const labels = element.querySelectorAll<HTMLElement>('.annotation-label');
    labels.forEach((label) => {
        const style = window.getComputedStyle(label);
        if (style.display === 'none' || style.visibility === 'hidden' || style.opacity === '0') {
            return;
        }

        const rect = label.getBoundingClientRect();
        const localX = rect.left - overlayRect.left;
        const localY = rect.top - overlayRect.top;

        context.fillStyle = style.backgroundColor || 'rgba(20,22,27,0.82)';
        context.strokeStyle = style.borderColor || 'rgba(255,255,255,0.24)';
        context.lineWidth = Number.parseFloat(style.borderWidth || '1') || 1;
        context.fillRect(localX, localY, rect.width, rect.height);
        context.strokeRect(localX, localY, rect.width, rect.height);

        const textElement = label.querySelector<HTMLElement>('.annotation-label-text');
        const badgeElement = label.querySelector<HTMLElement>('.annotation-label-exhibit');
        const labelText = textElement?.textContent?.trim() || '';
        const badgeText = badgeElement?.textContent?.trim() || '';

        context.fillStyle = style.color || '#ffffff';
        context.font = `${style.fontWeight} ${style.fontSize} ${style.fontFamily}`;
        context.textBaseline = 'middle';

        const paddingX = 8;
        const textY = localY + rect.height * 0.5;
        context.fillText(labelText, localX + paddingX, textY);

        if (badgeText.length > 0) {
            const labelWidth = context.measureText(labelText).width;
            const badgeX = localX + paddingX + labelWidth + 10;
            const badgeStyle = badgeElement ? window.getComputedStyle(badgeElement) : null;
            const badgeHeight = Math.max(16, rect.height - 8);
            const badgeWidth = Math.max(22, context.measureText(badgeText).width + 10);
            const badgeY = localY + (rect.height - badgeHeight) * 0.5;

            context.fillStyle = badgeStyle?.backgroundColor || 'rgba(255,197,58,0.24)';
            context.fillRect(badgeX, badgeY, badgeWidth, badgeHeight);

            context.fillStyle = badgeStyle?.color || style.color || '#ffffff';
            context.fillText(badgeText, badgeX + 5, textY);
        }
    });

    context.restore();
};

const clampSize = (
    baseSize: { width: number; height: number },
    requestedScale: 2 | 4,
    maxDimension: number
) => {
    const targetWidth = Math.max(1, Math.round(baseSize.width * requestedScale));
    const targetHeight = Math.max(1, Math.round(baseSize.height * requestedScale));

    const maxTargetDimension = Math.max(targetWidth, targetHeight);
    if (maxTargetDimension <= maxDimension) {
        return {
            width: targetWidth,
            height: targetHeight,
            appliedScale: requestedScale
        };
    }

    const ratio = maxDimension / maxTargetDimension;
    const width = Math.max(1, Math.floor(targetWidth * ratio));
    const height = Math.max(1, Math.floor(targetHeight * ratio));
    const appliedScale = Math.min(width / baseSize.width, height / baseSize.height);

    return {
        width,
        height,
        appliedScale
    };
};

const toSafeNumber = (value: unknown) => {
    return typeof value === 'number' && Number.isFinite(value) ? value : 0;
};

const registerScreenshotEvents = (scene: Scene, events: Events) => {
    events.function('screenshot.capture', async (options?: ScreenshotCaptureOptions): Promise<ScreenshotCaptureResult> => {
        const requestedScale = options?.scale === 4 ? 4 : 2;
        const includeOverlays = options?.includeOverlays !== false;
        const shouldDownload = options?.download !== false;
        const shouldReturnBlob = options?.returnBlob === true;

        const canvas = document.getElementById('canvas') as HTMLCanvasElement | null;
        const canvasRect = readElementRect(canvas);
        if (!canvas || !canvasRect) {
            throw new Error('canvas is not available for screenshot capture');
        }

        const maxDimension = Math.max(2048, scene.app.graphicsDevice.maxTextureSize || 8192);
        const outputSize = clampSize(canvasRect, requestedScale, maxDimension);

        const pixels = await events.invoke('render.offscreen', outputSize.width, outputSize.height) as Uint8Array;
        const outputCanvas = document.createElement('canvas');
        outputCanvas.width = outputSize.width;
        outputCanvas.height = outputSize.height;

        const context = outputCanvas.getContext('2d');
        if (!context) {
            throw new Error('unable to create screenshot canvas context');
        }

        const imageData = new ImageData(new Uint8ClampedArray(pixels), outputSize.width, outputSize.height);
        context.putImageData(imageData, 0, 0);

        const measurementOverlay = document.querySelector<SVGSVGElement>('svg#measure-tool-svg');
        const annotationOverlay = document.getElementById('annotation-labels-overlay') as HTMLDivElement | null;

        const measurementOverlayIncluded = includeOverlays && isElementRendered(measurementOverlay);
        const annotationOverlayIncluded = includeOverlays && isElementRendered(annotationOverlay);

        if (measurementOverlayIncluded && measurementOverlay) {
            try {
                await drawSvgOverlay(context, measurementOverlay, canvasRect, outputSize);
            } catch (error) {
                console.warn('Failed to draw measurement overlay in screenshot', error);
            }
        }

        if (annotationOverlayIncluded && annotationOverlay) {
            try {
                drawHtmlOverlay(context, annotationOverlay, canvasRect, outputSize);
            } catch (error) {
                console.warn('Failed to draw annotation overlay in screenshot', error);
            }
        }

        const pngBlob = await new Promise<Blob>((resolve, reject) => {
            outputCanvas.toBlob((blob) => {
                if (!blob) {
                    reject(new Error('failed to encode screenshot PNG'));
                    return;
                }
                resolve(blob);
            }, 'image/png');
        });

        const caseMetadata = (events.invoke('case.metadata') ?? {}) as CaseMetadata;
        const cameraPose = (events.invoke('camera.getPose') ?? {}) as CameraPose;
        const layers = (events.invoke('annotations.layers') ?? []) as AnnotationLayer[];

        const metadata: ScreenshotMetadata = {
            metadataMode: 'sidecar-json',
            caseName: caseMetadata.caseName,
            caseNumber: caseMetadata.caseNumber,
            captureDate: new Date().toISOString(),
            camera: {
                position: {
                    x: toSafeNumber(cameraPose?.position?.x),
                    y: toSafeNumber(cameraPose?.position?.y),
                    z: toSafeNumber(cameraPose?.position?.z)
                },
                target: {
                    x: toSafeNumber(cameraPose?.target?.x),
                    y: toSafeNumber(cameraPose?.target?.y),
                    z: toSafeNumber(cameraPose?.target?.z)
                }
            },
            visibleLayers: layers.filter(layer => layer?.visible !== false).map(layer => layer?.name || layer?.id || '').filter(name => name.length > 0),
            overlays: {
                annotationLabels: annotationOverlayIncluded,
                measurementOverlay: measurementOverlayIncluded
            }
        };

        const now = new Date();
        const caseSegment = sanitizeFileSegment(caseMetadata.caseNumber || caseMetadata.caseName || 'case');
        const stamp = formatTimestamp(now);
        const imageFileName = `${caseSegment}-screenshot-${stamp}.png`;
        const metadataFileName = `${caseSegment}-screenshot-${stamp}.json`;

        if (shouldDownload) {
            createDownload(pngBlob, imageFileName);
            const metadataBlob = new Blob([JSON.stringify(metadata, null, 2)], { type: 'application/json' });
            createDownload(metadataBlob, metadataFileName);
        }

        return {
            success: true,
            metadataMode: 'sidecar-json',
            baseSize: canvasRect,
            outputSize: { width: outputSize.width, height: outputSize.height },
            requestedScale,
            appliedScale: outputSize.appliedScale,
            maxDimension,
            imageFileName,
            metadataFileName,
            metadata,
            pngBlob: shouldReturnBlob ? pngBlob : undefined
        };
    });

    events.on('screenshot.capture', async () => {
        events.fire('startSpinner');

        try {
            await events.invoke('screenshot.capture');
        } catch (error) {
            await events.invoke('showPopup', {
                type: 'error',
                header: 'Screenshot capture failed',
                message: `'${(error as any)?.message ?? error}'`
            });
        } finally {
            events.fire('stopSpinner');
        }
    });
};

export { registerScreenshotEvents };
export type { ScreenshotCaptureOptions, ScreenshotCaptureResult, ScreenshotMetadata };
